// Versioned word lists. Each edition records the list version it was published with, so an old
// edition always replays against the same words. Published lists never change: fix a list by
// adding the next version.
import v1 from './lists/v1.ts'
import v2 from './lists/v2.ts'
import v3 from './lists/v3.ts'

export interface WordList {
  readonly version: number
  /** Uppercase five-letter words, sorted A–Z. */
  readonly words: readonly string[]
  /** Case-insensitive membership test. */
  has(word: string): boolean
}

const SOURCES: Readonly<Record<number, string>> = { 1: v1, 2: v2, 3: v3 }

export const WORDS_VERSIONS: readonly number[] = Object.keys(SOURCES).map(Number)
export const LATEST_WORDS_VERSION = Math.max(...WORDS_VERSIONS)

const cache = new Map<number, WordList>()

/** The published list for a version (the latest by default). Throws for an unknown version. */
export function getWordList(version: number = LATEST_WORDS_VERSION): WordList {
  let list = cache.get(version)
  if (!list) {
    const source = SOURCES[version]
    if (source === undefined) throw new RangeError(`Unknown word list version ${version}`)
    list = createWordList(version, source.split('\n'))
    cache.set(version, list)
  }
  return list
}

/** A list built from any words, for tests and experiments. Blank lines are skipped; anything that
 *  isn't five letters A–Z throws. Duplicates are dropped and the result is sorted. */
export function createWordList(version: number, words: Iterable<string>): WordList {
  const set = new Set<string>()
  for (const raw of words) {
    const w = raw.trim().toUpperCase()
    if (!w) continue
    if (!/^[A-Z]{5}$/.test(w)) throw new TypeError(`Not a five-letter A–Z word: ${JSON.stringify(raw)}`)
    set.add(w)
  }
  const sorted = Object.freeze([...set].sort())
  return Object.freeze({
    version,
    words: sorted,
    has: (word: string) => set.has(word.toUpperCase()),
  })
}
